import { MODIFIER_DEFS, type ChallengeModifierId } from '@cradle7/shared';
import { useT, type TKey } from '../i18n';

type Props = { modifierId: ChallengeModifierId };

/**
 * Compact gold-rim badge anchored to the HUD top-right corner during a
 * challenge run. Surfaces the active modifier + score multiplier so the
 * player remembers the rule-twist mid-fight.
 *
 * i18n: modifier name resolved via `t('challenge.modifier.{id}.name')`;
 * header + multiplier line use `t('challenge.hud.*')`. The `{mult}`
 * placeholder follows the project-wide `.replace('{token}', value)` pattern
 * (see PerkChoice/EndScreen) — the helper `t()` has no built-in interpolator.
 */
export function ChallengeBadge({ modifierId }: Props) {
  const { t } = useT();
  const def = MODIFIER_DEFS[modifierId];
  const nameKey = `challenge.modifier.${modifierId}.name` as TKey;
  return (
    <div
      data-testid="challenge-badge"
      style={{
        position: 'fixed',
        top: 12,
        right: 12,
        zIndex: 20,
        padding: '8px 12px',
        background: 'linear-gradient(180deg, #1d2942 0%, #0a0f1c 100%)',
        border: '1px solid rgba(245,197,66,0.5)',
        borderRadius: 8,
        boxShadow:
          '0 0 18px rgba(245,197,66,0.18) inset, 0 6px 14px rgba(0,0,0,0.5)',
        minWidth: 140,
      }}
    >
      <div
        style={{
          fontFamily: '"Russo One", sans-serif',
          fontSize: 9,
          letterSpacing: '0.16em',
          color: 'var(--gold-hi, #ffd866)',
          marginBottom: 4,
        }}
      >
        {t('challenge.hud.label')}
      </div>
      <div
        style={{
          fontFamily: '"Russo One", sans-serif',
          fontSize: 11,
          letterSpacing: '0.06em',
          color: '#f5f5f5',
        }}
      >
        {t(nameKey)}
      </div>
      <div
        style={{
          fontFamily: '"Manrope", sans-serif',
          fontSize: 10,
          color: 'rgba(255,255,255,0.6)',
          marginTop: 2,
          textAlign: 'right',
        }}
      >
        {t('challenge.hud.multiplier').replace('{mult}', def.scoreMultiplier.toFixed(2))}
      </div>
    </div>
  );
}
